import { useMemo } from 'react';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { getContext } from '@fiverr-private/fiverr_context';
import type { ListingFlow, UserData } from '@fiverr-private/listing_types';
import { LISTING_FLOWS, PRO_LISTING_FLOWS } from '@fiverr-private/dm_brief_creation';
import { EXPERIMENTS } from '@fiverr-private/listing_experiments';
import { calculateGroup } from '@fiverr-private/experiments';
import { MAX_ITEMS_TO_SHOW_NO_RESULTS } from '../../constants';
import { EXCLUDED_SELLERS_COUNTRIES_CODES } from './constants';

export interface UseInBriefFloatingFormPreConditionsArgs {
    flow: ListingFlow;
    userType: UserData['userType'];
    isExpertListings: boolean;
    resultsCount: number;
    inCrowsMpBffExpansionRollout: boolean;
}

export const useInBriefFloatingFormPreConditions = ({
    flow,
    userType,
    isExpertListings,
    resultsCount,
    inCrowsMpBffExpansionRollout,
}: UseInBriefFloatingFormPreConditionsArgs): boolean => {
    const {
        userId,
        experience: { isBusiness },
        isMobile,
        locale,
        countryCode,
    } = getContext();

    const inCrowsMpBffExpansionExperiment = useMemo(() => {
        const context = getContext();
        if (!inCrowsMpBffExpansionRollout || !context.userId) {
            return false;
        } else {
            const group =
                EXPERIMENTS.CROWS_MP_BFF_EXPANSION in context.abTests
                    ? Number(context.abTests[EXPERIMENTS.CROWS_MP_BFF_EXPANSION])
                    : calculateGroup({
                          experimentId: EXPERIMENTS.CROWS_MP_BFF_EXPANSION,
                          experimentType: 'user',
                          numOfGroups: 2,
                          context,
                      });
            return Number(group) === 2;
        }
    }, [inCrowsMpBffExpansionRollout]);

    const hasFewResults = isExpertListings ? resultsCount < MAX_ITEMS_TO_SHOW_NO_RESULTS : resultsCount === 0;
    const businessCondition = inCrowsMpBffExpansionExperiment ? true : isBusiness;
    const userTypeCondition = inCrowsMpBffExpansionExperiment
        ? userType !== 'REGISTERED_NOT_CONVERTED'
        : userType === 'REPEAT_BUYER';
    const flowCondition = ((inCrowsMpBffExpansionExperiment ? LISTING_FLOWS : PRO_LISTING_FLOWS) as string[]).includes(
        flow
    );

    return (
        !!userId &&
        !isMobile &&
        locale === DEFAULT_FIVERR_LOCALE &&
        !EXCLUDED_SELLERS_COUNTRIES_CODES.includes(countryCode) &&
        !hasFewResults &&
        businessCondition &&
        userTypeCondition &&
        flowCondition
    );
};
